/**
 * WHAT A CHECK FOUND, IN THE OFFICER'S WORDS.
 *
 * The checks on a draft speak the engine's language: "Forbidden token in the
 * signed order (rupee_symbol)", "non-terminal verdict", "machinery vocabulary".
 * Those messages are written for the drafting loop that repairs a section, and
 * to an adjudicating officer they say nothing — "token" is not a word of his
 * trade. A next step is addressed to HIM: what is wrong with the order, in the
 * words he would use to a colleague, and what the law or the office's practice
 * expects instead.
 *
 * One entry per finding the gate can make (`backend/oio_service/nodes/
 * compile_oio/gate.py`), and one per kind of stray text its lexicon looks for
 * (`nodes/case_spine/tone_digest.py`). `next-steps-copy.test.ts` reads both
 * files and fails when a finding exists with no wording here — so a new check
 * cannot reach an officer as a code.
 *
 * RULES FOR WRITING ONE. The title is one line and states the defect as a fact
 * about the order. `why` is one or two sentences: the rule (with its section
 * where there is one) and what to do. No code names, no "token", "machinery",
 * "pipeline", "verdict state", "render", "lexicon", "spine".
 */
export interface StepCopy {
  title: string
  why: string
}

/** Stray text the order must not carry, by the name the lexicon gives the pattern. */
const STRAY_TEXT: Record<string, StepCopy> = {
  rupee_symbol: {
    title: "Amounts are written with the ₹ symbol",
    why: "Orders of this office write amounts as “Rs. 1,00,000”. Replace the symbol where it appears.",
  },
  bare_zero_rupee: {
    title: "An amount is written with a single decimal place",
    why: "Write the amount in full, as “Rs. 1,250” or “Rs. 1,250.00”, not “Rs. 1,250.0”.",
  },
  doubled_token: {
    title: "A word is printed twice in a row",
    why: "A word such as “Section”, “Rule” or “Penalty” is repeated (“Section Section 74”). Remove the repetition.",
  },
  doubled_interest_label: {
    title: "“Interest” is printed twice in a row",
    why: "The line reads “Interest: Interest”. Remove the repetition.",
  },
  contention_id: {
    title: "A contention is referred to by a reference code instead of in words",
    why: "The order cites the noticee’s contention by a drafting reference (such as “A1:C2”). Describe the contention in words, as an order does.",
  },
  allegation_reference: {
    title: "A charge is referred to by a reference code instead of in words",
    why: "The order says “reference A1” where it should name the charge. Describe it in words.",
  },
  system_vocabulary: {
    title: "A sentence describes how the draft was prepared",
    why: "An order speaks in the adjudicating authority’s voice and never about the tool that helped draft it. Remove or reword the sentence.",
  },
  null_particular: {
    title: "A particular is blank and prints as “null”",
    why: "A detail could not be read from the record. Enter it, or remove the line, before signing.",
  },
  register_id_leak: {
    title: "A document is cited by a reference code instead of by name",
    why: "The order refers to a piece of evidence as “E3” or “FIG5”. Name the document (the invoice, the ledger, the statement) instead.",
  },
  register_tier_vocabulary: {
    title: "Evidence is described with grading labels instead of ordinary words",
    why: "Phrases such as “tier-1 evidence” are working labels. Say what the document is and why it is or is not reliable.",
  },
  prose_tier_vocabulary: {
    title: "Evidence is described with grading labels instead of ordinary words",
    why: "Phrases such as “higher-tier evidence” or “bare assertion” are working labels. Say what the document is and why it is or is not reliable.",
  },
  placeholder_bracket: {
    title: "A blank is left in the order",
    why: "Text in square brackets is still to be filled in. Complete it or remove it: an order cannot be signed with a blank.",
  },
  placeholder_deferral: {
    title: "A blank is left in the order",
    why: "A passage says something is “to be recorded” or “to be entered”. Complete it or remove it before signing.",
  },
  review_heading: {
    title: "A note meant for you is printed inside the order",
    why: "Review notes belong in this panel, not in the signed order. Remove the heading and what follows it.",
  },
}

/** Findings by their code; a code of the form "family:rule" is looked up whole, then by family. */
const FINDINGS: Record<string, StepCopy> = {
  non_terminal_verdict: {
    title: "A charge has not been decided",
    why: "An order must confirm, partly confirm or drop every charge in the notice. The analysis of this charge did not complete, so the order does not decide it. Decide it before signing.",
  },
  intent_scope_conflict: {
    title: "The finding on fraud or suppression is not applied consistently",
    why: "The order’s finding on intent under section 74 and the finding on this charge point in different directions. They must agree, or the order must say why this charge is different.",
  },
  tax_unreachable_on_penalty_only_notice: {
    title: "Tax is confirmed, but the notice proposed only a penalty",
    why: "A demand cannot go beyond the notice (section 75(7)). Where the notice proposed no tax, none can be confirmed in these proceedings.",
  },
  "per_se_legal_error:s155_burden_inverted": {
    title: "The burden of proof is placed on the wrong side",
    why: "Section 155 places the burden of proving eligibility for input tax credit on the person who claims it. The order states it the other way.",
  },
  "per_se_legal_error:penalty_on_absent_tax": {
    title: "A penalty is imposed where no tax is found payable",
    why: "A penalty measured on tax cannot stand where the order itself finds no tax due on the charge.",
  },
  "per_se_legal_error:mens_rea_disclaimed": {
    title: "A penalty that requires intent is confirmed while the order says intent is not needed",
    why: "Sections 74, 74A and 122 rest on fraud, wilful misstatement or suppression. The order cannot confirm under them and also say intent is immaterial.",
  },
  per_se_legal_error: {
    title: "A statement in the order is wrong in law on its face",
    why: "The passage states the law in a way no reading of the record can support. Correct it before signing.",
  },
  procedural_request_undisposed: {
    title: "A request made by the noticee has not been ruled on",
    why: "A request for cross-examination, documents or more time must be allowed or refused with reasons. One left unanswered is a ground of natural justice in appeal.",
  },
  demand_mismatch: {
    title: "The amounts in the order do not add up to the findings",
    why: "The demand table and the findings on the charges give different totals. Reconcile them so the ORDER confirms exactly what the discussion finds.",
  },
  demand_table_missing: {
    title: "The order has no table of the demand confirmed",
    why: "State the tax, interest and penalty confirmed for each charge and period in a table, so the demand can be read at a glance and recovered.",
  },
  demand_ceiling_exceeded: {
    title: "The demand confirmed is more than the notice proposed",
    why: "No demand may exceed the amount specified in the notice (section 75(7)). Bring the confirmed amount within the notice. Interest is not counted.",
  },
  demand_ceiling_unavailable: {
    title: "The amount proposed in the notice could not be read",
    why: "So the check that the demand stays within the notice (section 75(7)) was not made. Compare the two yourself.",
  },
  penalty_above_proposed: {
    title: "The penalty is higher than the notice proposed",
    why: "Check the penalty against the notice: what is imposed should not exceed what was proposed (section 75(7)).",
  },
  limitation_bind: {
    title: "A demand is confirmed for a period that is time-barred",
    why: "Every year covered by this demand is past the time limit for an order under its section, and proceedings for a barred year are deemed concluded (section 75(10)).",
  },
  limitation_overlap: {
    title: "Part of the period confirmed is time-barred",
    why: "Some of the years covered by this demand are past the time limit for an order under its section. Confine the demand to the years still open.",
  },
  regime_mismatch: {
    title: "The section applied does not govern the financial year",
    why: "Sections 73 and 74 apply to years up to 2023-24; section 74A alone applies from 2024-25. Apply the section that governs the year.",
  },
  grounds_scope: {
    title: "A demand rests on a ground the notice did not raise",
    why: "An order cannot confirm a demand on a ground or section other than those specified in the notice (section 75(7)).",
  },
  double_penalty: {
    title: "Two penalties are imposed for the same act",
    why: "Once a penalty is imposed under section 73, 74 or 74A, no penalty for the same act may be imposed under any other provision (section 75(13)).",
  },
  alternative_double_confirmation: {
    title: "The same amount is confirmed twice under alternative charges",
    why: "Where the notice puts one amount under alternative charges, only one of them can sustain the demand. Confirm it once.",
  },
  missing_section: {
    title: "A part of the order is missing",
    why: "The order must carry every part: the facts, the noticee’s submissions, the hearing, the discussion and findings, and the ORDER.",
  },
  unexpected_section: {
    title: "The order has a part it should not have",
    why: "Remove the extra part, or move what it says to where it belongs.",
  },
  contention_uncovered: {
    title: "A contention of the noticee is not answered",
    why: "A speaking order deals with every contention raised in the reply. One left unanswered is the commonest ground on which an order is set aside.",
  },
  contention_unaddressed: {
    title: "A contention of the noticee is answered only in passing",
    why: "The discussion mentions the contention without meeting it. Answer it on the facts and the law.",
  },
  governing_section_mismatch: {
    title: "The section under which the order is passed is not stated as the findings hold",
    why: "The discussion should close by stating the section under which each demand is confirmed, in the terms the findings use.",
  },
  entity_name_missing: {
    title: "The noticee’s name is missing from the order",
    why: "State the noticee’s name as it stands in the notice and the registration.",
  },
  entity_gstin_missing: {
    title: "The noticee’s GSTIN is missing from the order",
    why: "State the GSTIN, so the order can be served and recovered against the right registration.",
  },
  entity_name_underivable: {
    title: "The noticee’s name could not be read from the record",
    why: "Check the name in the order against the notice.",
  },
  appeal_clause_mismatch: {
    title: "The paragraph on appeal does not match the order",
    why: "The appellate authority, the form and the time limit stated must be those that apply to this order.",
  },
  retired_appeal_authority: {
    title: "The paragraph on appeal names an authority that no longer hears these appeals",
    why: "Name the appellate authority that has jurisdiction today.",
  },
  operative_clause_altered: {
    title: "The ORDER does not say what the findings decide",
    why: "The operative part must confirm or drop each charge in the amounts the discussion arrives at, and nothing else.",
  },
  order_direction_missing: {
    title: "The ORDER gives no direction on a charge",
    why: "For every charge the ORDER must say what is confirmed and to be paid, or that the charge is dropped.",
  },
  ex_parte_without_hearing_log: {
    title: "An ex-parte order needs the record of the hearings offered",
    why: "Where no one appeared, the order must recite each opportunity given — the dates of the letters and of the hearings fixed — and that none was availed. It is the paragraph most often attacked in appeal.",
  },
  hearing_recital_incomplete: {
    title: "The paragraph on the personal hearing is incomplete",
    why: "Give the date of the hearing letter, the date of the hearing, and who attended or that no one did.",
  },
  hearing_not_on_record: {
    title: "No personal hearing is on record",
    why: "A hearing must be granted before any adverse order (section 75(4)). Check the hearing record and recite it in the order.",
  },
  din_rfn_missing: {
    title: "The order carries no DIN or portal reference number",
    why: "A communication needs a Document Identification Number, or the reference number the common portal generates (Circulars 128/2019 and 249/06/2025).",
  },
  drop_without_ground_traversal: {
    title: "A charge is dropped without meeting the department’s ground for it",
    why: "A charge can be dropped on a contention that answers the ground on which it was raised, or because that ground itself fails — not on a reply to a different point.",
  },
  uncited_authority: {
    title: "A judgment is relied on that is not among the verified citations",
    why: "Every case the order relies on should be one you have checked. Verify it in Analysis, or remove the reliance.",
  },
  officer_remit: {
    title: "The order decides a constitutional question",
    why: "An adjudicating authority cannot hold a provision unconstitutional. The point can be noted as raised and left to the appropriate forum.",
  },
  narrative_thread_invalid: {
    title: "The discussion does not run from the facts to the finding",
    why: "The reasoning on the charges should follow one line: what was alleged, what the record shows, what the law requires, and the finding.",
  },
  extended_period_authority_out_of_scope: {
    title: "A section 73 demand relies on case law about suppression and the extended period",
    why: "Those judgments test fraud and suppression under section 74. They bring the wrong test into a demand confirmed under section 73.",
  },
  s75_2_revenue_route: {
    title: "A charge is dropped for want of fraud: see whether a section 73 demand is still open",
    why: "Where fraud or suppression is not established the tax is to be determined as under section 73 (section 75(2)). The note shows, year by year, whether that is still within time.",
  },
  discussion_compressed: {
    title: "The discussion of a charge is too brief for a speaking order",
    why: "Set out the charge, the evidence, the defence, the law and the finding in separate paragraphs, so each can be cited and tested.",
  },
  signed_order_lexicon: {
    title: "The order contains text that does not belong in a signed order",
    why: "Remove or reword the passage shown.",
  },
}

/** The officer's wording for a finding, or `null` when none has been written for it. */
export function copyFor(code: string): StepCopy | null {
  const at = code.indexOf(":")
  const family = at < 0 ? code : code.slice(0, at)
  const rule = at < 0 ? "" : code.slice(at + 1)
  if (family === "signed_order_lexicon" && STRAY_TEXT[rule]) return STRAY_TEXT[rule]
  return FINDINGS[code] ?? FINDINGS[family] ?? null
}

/** Every code and stray-text name with wording — for the test that keeps this file complete. */
export const COPY_KEYS = { findings: Object.keys(FINDINGS), strayText: Object.keys(STRAY_TEXT) }
